import { CoreError, assertSafeInt } from './errors'
import type { BuildContext, NumberingInput } from './documents'
import type { Paisa } from './money'
import type { Milli } from './qty'
import { numberFields, purchaseLineCost } from './purchase-orders'
import type {
  PurchaseRequestLineRow,
  PurchaseRequestRow,
  PurchaseRequestStatus,
  SyncOp,
  SyncRowBase,
} from './rows'

declare const crypto: { randomUUID(): string }

// Purchase requests (docs/plans/purchase-requests.md): someone asks for stock, an approver
// says yes, and the approved lines become purchase order lines that point back at them.
// Paperwork only: nothing here moves stock, and how much of a request is ordered is summed
// from the order lines, never stored.

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

// approved (after submitted) and rejected are set by the server's decide_document.
const MOVES: Partial<Record<PurchaseRequestStatus, PurchaseRequestStatus[]>> = {
  submitted: ['cancelled'],
  approved: ['closed', 'cancelled'],
}

/** The status moves a device may make after draft. */
export function canMovePurchaseRequest(
  request: Pick<PurchaseRequestRow, 'status'>,
  to: PurchaseRequestStatus,
): boolean {
  return MOVES[request.status]?.includes(to) ?? false
}

export interface PurchaseRequestLineInput {
  /** Null: written in, with `description`. */
  productId: string | null
  description?: string | null
  qtyMilli: Milli
  estCostPaisa: Paisa
}

export interface BuildPurchaseRequestInput {
  id?: string
  /**
   * `submitted` when a step of approval applies to the total, `approved` when none does
   * (both are numbered).
   */
  status: 'draft' | 'submitted' | 'approved'
  locationId?: string | null
  requestDate: string
  neededBy?: string | null
  notes?: string | null
  lines: readonly PurchaseRequestLineInput[]
  /** Needed once it leaves draft. */
  numbering?: NumberingInput
}

export interface BuiltPurchaseRequest {
  request: PurchaseRequestRow
  lines: PurchaseRequestLineRow[]
  ops: SyncOp[]
}

/**
 * A purchase request with its lines, as one tx. Saving a draft again with the same `id`
 * replaces it: pass the stored lines as `replaces` so the ones that are gone are deleted.
 */
export function buildPurchaseRequest(
  ctx: BuildContext,
  input: BuildPurchaseRequestInput,
  replaces: readonly Pick<PurchaseRequestLineRow, 'id'>[] = [],
): BuiltPurchaseRequest {
  if (input.lines.length === 0)
    throw new CoreError('invalid_request', 'a purchase request needs at least one line')
  for (const l of input.lines) {
    assertSafeInt(l.qtyMilli, 'qtyMilli')
    assertSafeInt(l.estCostPaisa, 'estCostPaisa')
    if (l.qtyMilli <= 0) throw new CoreError('invalid_line', 'quantity must be positive')
    if (l.estCostPaisa < 0) throw new CoreError('invalid_line', 'cost cannot be negative')
    if (!l.productId && !l.description?.trim())
      throw new CoreError('invalid_line', 'a written-in line needs a description')
  }
  const request: PurchaseRequestRow = {
    ...base(ctx, input.id),
    location_id: input.locationId ?? null,
    status: input.status,
    ...numberFields(input.numbering, input.status !== 'draft'),
    request_date: input.requestDate,
    needed_by: input.neededBy ?? null,
    notes: input.notes?.trim() || null,
    total_paisa: input.lines.reduce((sum, l) => sum + requestLineCost(l), 0),
    closed_at: null,
    close_reason: null,
  }
  const lines: PurchaseRequestLineRow[] = input.lines.map((l, i) => ({
    ...base(ctx),
    purchase_request_id: request.id,
    line_no: i + 1,
    product_id: l.productId,
    description: l.productId ? null : l.description!.trim(),
    qty_milli: l.qtyMilli,
    est_cost_paisa: l.estCostPaisa,
  }))
  return {
    request,
    lines,
    ops: [
      op('purchase_requests', request),
      ...replaces.map((r): SyncOp => ({
        table: 'purchase_request_lines',
        op: 'delete',
        row: { id: r.id },
      })),
      ...lines.map((r) => op('purchase_request_lines', r)),
    ],
  }
}

/** A line's estimate: round(qty × cost), as the server adds the request up. */
export function requestLineCost(l: Pick<PurchaseRequestLineInput, 'qtyMilli' | 'estCostPaisa'>) {
  return purchaseLineCost({ qtyMilli: l.qtyMilli, unitCostPaisa: l.estCostPaisa })
}

/** Closes the rest of an approved request, or cancels one, with a reason. */
export function setPurchaseRequestStatus(
  ctx: BuildContext,
  request: PurchaseRequestRow,
  to: 'closed' | 'cancelled',
  reason: string,
): { request: PurchaseRequestRow; ops: SyncOp[] } {
  if (!canMovePurchaseRequest(request, to))
    throw new CoreError(
      'invalid_transition',
      `a ${request.status} purchase request cannot become ${to}`,
    )
  const why = reason.trim()
  if (!why) throw new CoreError('invalid_input', 'a reason is required')
  const next: PurchaseRequestRow = {
    ...request,
    status: to,
    closed_at: ctx.now,
    close_reason: why,
    updated_at: ctx.now,
  }
  return { request: next, ops: [op('purchase_requests', next)] }
}

/** Ordered so far, by request line id (from order lines on orders not cancelled or rejected). */
export type OrderedByLine = Readonly<Record<string, Milli>>

export type RequestProgress = 'none' | 'part' | 'full'

/** How much of an approved request is on purchase orders. */
export function requestProgress(
  lines: readonly Pick<PurchaseRequestLineRow, 'id' | 'qty_milli'>[],
  ordered: OrderedByLine,
): RequestProgress {
  const done = lines.map((l) => Math.min(ordered[l.id] ?? 0, l.qty_milli))
  const any = done.some((d) => d > 0)
  const all = lines.length > 0 && lines.every((l, i) => done[i]! >= l.qty_milli)
  return all ? 'full' : any ? 'part' : 'none'
}

/** What is left to order of one line, never below zero. */
export function requestLineLeft(
  line: Pick<PurchaseRequestLineRow, 'id' | 'qty_milli'>,
  ordered: OrderedByLine,
): Milli {
  return Math.max(0, line.qty_milli - (ordered[line.id] ?? 0))
}
